import { defineStore } from 'pinia';
import type PlayerRelation from "@/types/PlayerRelation.ts";
import emitter from '@/models/eventBus.ts';
import { useGameStore } from '@/stores/gameStore.ts'
import { useAllianceStore } from '@/stores/allianceStore.ts'

export const usePlayerRelationStore = defineStore('playerRelationsStore', {
    state: () => ({
        relations: new Map<number, PlayerRelation>(),
        onlinePlayers: new Map<number, number>(),
        onlinePlayersServerTimestamp: 0 as number
    }),
    getters: {
        findRelationByPlayerId: (state) => (playerId: number): PlayerRelation => {
            const relation = state.relations.get(playerId);
            if (relation) {
                return relation;
            } else {
                // Return a default value matching the PlayerRelation type
                return {
                    relation: "unknown",
                    name: "Unknown",
                    playerId: 0,
                    iRequested: 'none',
                    otherRequested: 'none',
                    profileImage: "",
                    napExpireTurn: 0,
                    country: "",
                    stopNap: "continue",
                    patreonTier: "",
                };
            }
        },
        isPlayerOnline: (state) => (playerId: number): boolean => {
            const playerTimestamp = state.onlinePlayers.get(playerId);
            if (!playerTimestamp || !state.onlinePlayersServerTimestamp) {
                return false;
            }
            return (state.onlinePlayersServerTimestamp - playerTimestamp) <= 300;
        },
        countByRelation: (state) => (relation: string) => {
            return Array.from(state.relations.values()).filter((value) => value.relation === relation).length;
        },
        allRelationsArray: (state) => {
            return Array.from(state.relations.entries());
        },
        allEnemyRelationsArray: (state) => {
            return Array.from(state.relations.entries()).filter(([, value]) => value.relation === "enemy");
        },
        getAllRelationsByType: (state) => (type: string) => {
            return Array.from(state.relations.entries()).filter(([, value]) => value.relation === type);
        },
        getAllRelationsWithPendingActions: (state) => () => {
            const counts = new Map<string, number>();

            Array.from(state.relations.values()).forEach((relation) => {
                if (relation.otherRequested !== 'none') {
                    if (!counts.has(relation.relation)) {
                        counts.set(relation.relation, 0);
                    }
                    counts.set(relation.relation, counts.get(relation.relation)! + 1);
                }
            });

            return counts;
        },
        getCountOfPendingActions: (state) => () => {
            let counts = 0;

            Array.from(state.relations.values()).forEach((relation) => {
                if (relation.otherRequested !== 'none') {
                    counts++;
                }
            });
            return counts;
        }
    },
    actions: {
        update(playerId: number, data: PlayerRelation) {
            this.relations.set(playerId, data);
            emitter.emit('mapRelationUpdate', data);
        },
        isEnemy(playerId: number):boolean {
            if(this.relations.has(playerId)) {
                const relation = this.relations.get(playerId);
                if (relation) {
                    return relation.relation === 'enemy';
                }
                return false;
            } else {
                return false;
            }
        },
        getProfileImageByPlayerId(playerId: number):string {

            const gameStore = useGameStore()

            if(this.relations.has(playerId)) {
                const relation = this.relations.get(playerId);
                if (relation) {
                    return relation.profileImage;
                }
                return '';
            } else if(playerId === gameStore.player!.id) {
                return gameStore.player!.profileImage;
            }
            return '';
        },
        getNameByPlayerId(playerId: number):string {
            if(this.relations.has(playerId)) {
                const relation = this.relations.get(playerId);
                if (relation) {
                    return relation.name;
                }
                return '';
            } else {
                return '';
            }
        },
        getCountryByPlayerId(playerId: number):string {
            const gameStore = useGameStore()
            
            if(this.relations.has(playerId)) {
                const relation = this.relations.get(playerId);
                if (relation) {
                    return relation.country;
                }
                return '';
            } else if(playerId === gameStore.player?.id) {
                return gameStore.player.country;
            }
            return '';
        },
        isAllianceLeader(playerId: number):boolean {
            const gameStore = useGameStore()

            const allianceStore = useAllianceStore()

            // Check if player is current user and is an alliance leader
            if(playerId === gameStore.player?.id && gameStore.player?.allianceId) {
                const alliance = allianceStore.getAlliance(gameStore.player.allianceId)
                return alliance?.leaderPlayerId === gameStore.player.id && alliance?.deleted !== 1
            }

            // Check if player is another player and is an alliance leader
            for (const alliance of allianceStore.alliances.values()) {
                if (alliance.leaderPlayerId === playerId && alliance.deleted !== 1) {
                    return true
                }
            }

            return false
        },
        updateOnlineStatus(onlinePlayersData: Record<string, number>, serverTimestamp: number) {
            this.onlinePlayers.clear()
            for (const [playerId, timestamp] of Object.entries(onlinePlayersData)) {
                this.onlinePlayers.set(Number(playerId), timestamp)
            }
            this.onlinePlayersServerTimestamp = serverTimestamp
        }
    }
});
